import { memo, useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

// jl-design-system
import Form from 'jl-design-system/components/form/Form';
import RadioButton from 'jl-design-system/components/radio-button/RadioButton';

// Types
import { AppDispatch } from 'types/RootState.types';

// Config
import { useWindowSize } from '@context/window/WindowSizeContext';
import { DeliveryChoiceRadioButtonsProps } from './DeliveryChoiceRadioButtons.types';
import deliveryChoiceRadioButtonsState from './DeliveryChoiceRadioButtons.state';
import { setSelectedDeliveryChoiceId } from '@redux/actions/delivery/deliveryActions';
import { getTabTitleWithCostInfo, getIcon } from '../deliveryChoiceUtils';

// Components
import Container from '@components/container';
import Heading from '@components/heading';
import BodyText from '@components/body-text';

import styles from './delivery-choice-radio-buttons.scss';

const isTabSelected = (deliveryId?: string, selectedDeliveryChoiceId?: string) =>
  deliveryId === selectedDeliveryChoiceId;

const Radio = memo(({ deliveryId, radioLabel, disabled, isSelected, cost, Icon, onChange }: any) => (
  <RadioButton
    checked={isSelected}
    className={styles.radio}
    disabled={disabled}
    focusOnLabel
    id={`${deliveryId}-tab`}
    label={(
      <>
        <Heading label={radioLabel} tag="h3" type="xs" />
        <BodyText className={styles.cost}>{cost}</BodyText>
        {Icon && <Icon className={styles.icon} />}
      </>
    )}
    labelClassName={cx(styles.label, {
      [styles.labelSelected]: isSelected,
    })}
    name="deliveryChoiceRadioButtons"
    onChange={onChange}
    value={deliveryId}
  />
));

const DeliveryChoiceRadioButtons = ({ tabs = [] }: DeliveryChoiceRadioButtonsProps) => {
  if (tabs?.length === 0) return null;

  const lastFocusedRef = useRef<string | null>(null);
  const memorisedTabs = useMemo(() => [...tabs], [tabs.map(tab => `${tab.deliveryId}:${tab.disabled}`).join(',')]);

  const dispatch: AppDispatch = useDispatch();

  const {
    grandTotalRaw,
    selectedDeliveryChoiceId,
    isDeliveryThresholdIncreaseEnabled,
  } = useSelector(deliveryChoiceRadioButtonsState);

  const { isSmallMobile } = useWindowSize();

  const onChangeHandler = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = evt?.target?.value;
    lastFocusedRef.current = currentValue;
    dispatch(setSelectedDeliveryChoiceId(currentValue));
  };

  const fields = useMemo(() => {
    return memorisedTabs.map(({ deliveryId, disabled, label }) => {
      const isSelected = isTabSelected(deliveryId, selectedDeliveryChoiceId);
      const cost = getTabTitleWithCostInfo(grandTotalRaw, deliveryId, true, isDeliveryThresholdIncreaseEnabled);
      const IconComponent = getIcon(deliveryId, isSelected);

      return {
        component: Radio,
        name: `${deliveryId}-field`,
        id: `${deliveryId}-field`,
        props: {
          deliveryId,
          radioLabel: label,
          disabled,
          isSelected,
          cost,
          Icon: IconComponent,
          onChange: onChangeHandler,
        },
      };
    });
  }, [memorisedTabs, grandTotalRaw, selectedDeliveryChoiceId]);

  useEffect(() => {
    if (lastFocusedRef.current) {
      const el = document.getElementById(`${lastFocusedRef.current}-tab`);
      if (el) {
        el.focus();
      }
    }
  }, [fields]);

  const formConfig = {
    fields,
    id: 'deliveryChoiceRadioButtons-form',
  };

  return (
    <Container className={styles.form} paddingX={isSmallMobile ? '2' : '3'} width="full">
      <Form config={formConfig} form={formConfig.id} />
    </Container>
  );
};

export default DeliveryChoiceRadioButtons;
